import { useEffect, useMemo, useState } from "react";
import { Download, Eye, Filter, RotateCcw } from "lucide-react";
import { API_EMPLOYEE_TECH_STACK_EXPORT } from "../../../config/Endpoints.js";

import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Card } from "../../components/shared/card/card.tsx";
import { EmptyStateMessage } from "../../components/shared/empty-state-message/empty-state-message.tsx";
import { EmployeeTableAvatar } from "../../components/features/approve-bank-details/components/employee-table-avatar.tsx";
import {
  Badge,
  Button,
  ResponsiveDataTable,
  Select,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  type ResponsiveColumn,
} from "../../components/ui/index.ts";
import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";
import { useEmployeeNameOptions } from "../../hooks/use-employee-name-options.ts";
import type { PageProps } from "../../types/page.ts";
import { showError } from "../../utils/toast.ts";
import { TechStackEditorModal } from "./tech-stack-editor-modal.tsx";
import type {
  EmployeeTechStackRow,
  TechStackFilters,
} from "./tech-stack-types.ts";
import {
  useTechStackList,
  useTechStackMaster,
} from "./use-tech-stack-list.ts";
import { CertificationListTab } from "./certifications/certification-list-tab.tsx";
import styles from "./tech-stack-list-page.module.css";
import { useSkillsExport } from "./use-skills-export.ts";

const EMPTY_FILTERS: TechStackFilters = {
  employeeId: "",
  categoryId: "",
  techStackId: "",
  techStackAssigned: "",
  technicalStatus: "",
  location: "",
};

export default function TechStackListPage({ title, pageMeta }: PageProps) {
  const { setBreadcrumbs } = useBreadcrumbs();
  const employees = useEmployeeNameOptions();
  const master = useTechStackMaster();
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [activeTab, setActiveTab] = useState("tech-stack");
  const [selectedEmployee, setSelectedEmployee] =
    useState<EmployeeTechStackRow | null>(null);
  const list = useTechStackList(filters, page, pageSize);
  const exportAction = useSkillsExport();
  const pageTitle = pageMeta?.title || title || "Employee Tech Stack";

  useEffect(() => {
    setBreadcrumbs([{ label: pageTitle }]);
    return () => setBreadcrumbs([]);
  }, [pageTitle, setBreadcrumbs]);

  useEffect(() => {
    const message = employees.error || master.error;
    if (message) showError(message);
  }, [employees.error, master.error]);

  useEffect(() => {
    if (list.error) showError(list.error);
  }, [list.error]);

  useEffect(() => {
    const allFiltersCleared =
      !draftFilters.employeeId &&
      !draftFilters.categoryId &&
      !draftFilters.techStackId &&
      !draftFilters.techStackAssigned &&
      !draftFilters.technicalStatus &&
      !draftFilters.location;
    const hasAppliedFilters =
      Boolean(filters.employeeId) ||
      Boolean(filters.categoryId) ||
      Boolean(filters.techStackId) ||
      Boolean(filters.techStackAssigned) ||
      Boolean(filters.technicalStatus) ||
      Boolean(filters.location);

    if (allFiltersCleared && hasAppliedFilters) {
      setFilters(EMPTY_FILTERS);
      setPage(1);
    }
  }, [
    draftFilters.employeeId,
    draftFilters.categoryId,
    draftFilters.techStackId,
    draftFilters.techStackAssigned,
    draftFilters.technicalStatus,
    draftFilters.location,
    filters.employeeId,
    filters.categoryId,
    filters.techStackId,
    filters.techStackAssigned,
    filters.technicalStatus,
    filters.location,
  ]);

  const categoryOptions = useMemo(
    () =>
      master.categories.map((category) => ({
        value: String(category.categoryId),
        label: category.category,
      })),
    [master.categories],
  );

  const techStackOptions = useMemo(() => {
    const selectedCategoryId = Number(draftFilters.categoryId);
    return master.categories
      .filter(
        (category) =>
          !draftFilters.categoryId || category.categoryId === selectedCategoryId,
      )
      .flatMap((category) =>
        category.techStacks.map((skill) => ({
          value: String(skill.techStackId),
          label: skill.techStack,
        })),
      );
  }, [draftFilters.categoryId, master.categories]);

  const columns = useMemo<ResponsiveColumn<EmployeeTechStackRow>[]>(
    () => [
      {
        id: "employeeName",
        header: "Employee",
        accessor: "employeeName",
        sortable: true,
        render: (row) => (
          <div className={styles.employeeCell}>
            <EmployeeTableAvatar
              employeeId={row.employeeId}
              name={row.employeeName}
              photoPath={row.photoPath ?? undefined}
              size="md"
            />
            <span>
              <strong className={styles.employeeName}>{row.employeeName}</strong>
              <small className={styles.employeeCode}>{row.employeeCode}</small>
            </span>
          </div>
        ),
      },
      {
        id: "designation",
        header: "Designation",
        accessor: "designation",
        sortable: true,
        truncate: true,
      },
      {
        id: "manager",
        header: "Reporting Manager",
        accessor: "manager",
        sortable: true,
        truncate: true,
      },
      {
        id: "techStacks",
        header: "Tech Skills",
        getTitleText: (row) =>
          row.techStacks.map((skill) => skill.techStack).join(", "),
        render: (row) =>
          row.techStacks.length ? (
            <div className={styles.badgeList}>
              {row.techStacks.slice(0, 3).map((skill) => (
                <Badge
                  key={skill.techStackId}
                  variant={skill.primarySkill ? "success" : "neutral"}
                >
                  {skill.techStack}
                  {skill.primarySkill ? " · Primary" : ""}
                </Badge>
              ))}
              {row.techStacks.length > 3 ? (
                <Badge variant="neutral">+{row.techStacks.length - 3}</Badge>
              ) : null}
            </div>
          ) : (
            <span className={styles.noSkills}>User not filled</span>
          ),
      },
      {
        id: "action",
        header: "Action",
        nowrap: true,
        render: (row) => (
          <Button
            size="sm"
            variant="accent-ghost"
            startIcon={<Eye size={16} aria-hidden />}
            onClick={() => setSelectedEmployee(row)}
          >
            View
          </Button>
        ),
      },
    ],
    [],
  );

  const lookupLoading = employees.loading || master.loading;
  const clearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const exportTechStacks = () => {
    const params = new URLSearchParams();
    if (draftFilters.employeeId) {
      params.set("employeeId", draftFilters.employeeId);
    }
    if (draftFilters.categoryId) {
      params.set("categoryId", draftFilters.categoryId);
    }
    if (draftFilters.techStackId) {
      params.set("techStackId", draftFilters.techStackId);
    }
    if (
      draftFilters.techStackAssigned === "true" ||
      draftFilters.techStackAssigned === "false"
    ) {
      params.set("techStackAssigned", draftFilters.techStackAssigned);
    }
    if (draftFilters.technicalStatus === "true" || draftFilters.technicalStatus === "false") {
      params.set("technicalStatus", draftFilters.technicalStatus);
    }
    if (draftFilters.location) params.set("location", draftFilters.location);
    void exportAction.exportExcel(
      API_EMPLOYEE_TECH_STACK_EXPORT,
      params,
      "Employee_Tech_Stack.xlsx",
    );
  };

  return (
    <PageShell className={styles.page}>
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        idPrefix="employee-skills"
      >
        <TabsList>
          <TabsTrigger value="tech-stack">Technical Skills</TabsTrigger>
          <TabsTrigger value="certifications">Certifications</TabsTrigger>
        </TabsList>

        <TabsContent value="tech-stack" className={styles.tabPanel}>
      <Card className={styles.filterCard}>
        <div className={styles.filters}>
          <Select
            id="tech-stack-employee-filter"
            label="Employee Name"
            placeholder={employees.loading ? "Loading..." : "Select Employee"}
            value={draftFilters.employeeId}
            onChange={(employeeId) =>
              setDraftFilters((current) => ({ ...current, employeeId }))
            }
            options={employees.options}
            disabled={employees.loading}
            searchable
            isClearable
          />
          <Select
            id="tech-stack-category-filter"
            label="Category"
            placeholder={master.loading ? "Loading..." : "Select Category"}
            value={draftFilters.categoryId}
            onChange={(categoryId) =>
              setDraftFilters((current) => ({
                ...current,
                categoryId,
                techStackId: "",
              }))
            }
            options={categoryOptions}
            disabled={master.loading}
            searchable
            isClearable
          />
          <Select
            id="tech-stack-filter"
            label="Tech Skills"
            placeholder={master.loading ? "Loading..." : "Select Tech Skills"}
            value={draftFilters.techStackId}
            onChange={(techStackId) =>
              setDraftFilters((current) => ({ ...current, techStackId }))
            }
            options={techStackOptions}
            disabled={master.loading}
            searchable
            isClearable
          />
          <Select
            id="techStackAssigned-filter"
            label="Tech Skills Status"
            placeholder="Select Status"
            value={draftFilters.techStackAssigned}
            onChange={(techStackAssigned) =>
              setDraftFilters((current) => ({ ...current, techStackAssigned }))
            }
            options={[
              { value: "true", label: "Assigned" },
              { value: "false", label: "User Not Filled" },
            ]}
            isClearable
          />
          <Select
            id="technical-status-filter"
            label="Technical Status"
            placeholder="Select Technical Status"
            value={draftFilters.technicalStatus}
            onChange={(technicalStatus) =>
              setDraftFilters((current) => ({ ...current, technicalStatus }))
            }
            options={[
              { value: "true", label: "Technical" },
              { value: "false", label: "Non Technical" },
            ]}
            isClearable
          />
          <Select
            id="location-filter"
            label="Location"
            placeholder="Select Location"
            value={draftFilters.location}
            onChange={(location) =>
              setDraftFilters((current) => ({ ...current, location }))
            }
            options={[
              { value: "India", label: "India" },
              { value: "US", label: "US" },
            ]}
            isClearable
          />
          <div className={styles.filterActions}>
            <Button
              startIcon={<Filter size={16} aria-hidden />}
              disabled={lookupLoading || list.loading}
              onClick={() => {
                setFilters({ ...draftFilters });
                setPage(1);
              }}
            >
              Apply Filters
            </Button>
            <Button
              variant="light-grey"
              startIcon={<RotateCcw size={16} aria-hidden />}
              disabled={lookupLoading || list.loading}
              onClick={clearFilters}
            >
              Clear
            </Button>
            <Button
              variant="accent"
              startIcon={<Download size={16} aria-hidden />}
              disabled={lookupLoading || list.loading || exportAction.exporting}
              onClick={exportTechStacks}
            >
              {exportAction.exporting ? "Exporting..." : "Export"}
            </Button>
          </div>
        </div>
      </Card>

      <Card padding="none" className={styles.tableCard}>
        {list.loading ? (
          <div className={styles.loading}>
            <Spinner size="lg" label="Loading employee tech stacks" />
          </div>
        ) : (
          <>
            <ResponsiveDataTable<EmployeeTechStackRow>
              columns={columns}
              data={list.rows}
              aria-label="Employee tech stack list"
              emptyState={
                <EmptyStateMessage message="No employees match the selected filters." />
              }
              pagination
              page={page}
              pageSize={pageSize}
              totalItems={list.totalElements}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              pageSizeOptions={[5, 10, 20, 30, 50]}
            />
          </>
        )}
      </Card>

      <TechStackEditorModal
        employee={selectedEmployee}
        categories={master.categories}
        onClose={() => setSelectedEmployee(null)}
        onSaved={list.refetch}
      />
        </TabsContent>

        <TabsContent value="certifications" className={styles.tabPanel}>
          <CertificationListTab />
        </TabsContent>
      </Tabs>
    </PageShell>
  );
}
